import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LeaveTribeModal from './LeaveTribeModal';
import Tooltip from '@/modules/core/client/components/Tooltip';

import * as api from '../api/tribes.api';
import type { MembershipUpdate, TribeSummary } from '../api/tribes.api';
import type { UserProfile } from '@/modules/users/client/types';

interface PresentationalProps {
  activeClassName?: string;
  className?: string;
  icon?: boolean;
  isMember: boolean;
  isLoading?: boolean;
  memberLabel?: string;
  tribe: TribeSummary;
  isLoggedIn: boolean;
  onToggle?: () => void;
}

interface JoinButtonProps extends Omit<
  PresentationalProps,
  'isMember' | 'isLoggedIn' | 'isLoading' | 'onToggle'
> {
  tribe: TribeSummary;
  user?: UserProfile;
  onUpdated?: (data: MembershipUpdate) => void;
}

const TooltipWithContent = Tooltip as unknown as React.ComponentType<{
  tooltip: React.ReactNode;
  placement: 'bottom';
  hidden?: boolean;
  id?: string;
  children: React.ReactNode;
}>;

function JoinButtonPresentational({
  className = 'btn btn-sm btn-default',
  activeClassName,
  icon = true,
  isMember,
  isLoading,
  memberLabel,
  tribe,
  isLoggedIn,
  onToggle,
}: PresentationalProps) {
  const { t } = useTranslation('circles') as {
    t: (key: string, options?: Record<string, unknown>) => string;
  };

  const ariaLabel = isMember
    ? (t('Leave circle') as string)
    : (t('Join ({{label}})', { label: tribe.label }) as string);
  const buttonLabel = isMember
    ? memberLabel || (t('Joined') as string)
    : (t('Join') as string);
  const currentClassName =
    isMember && activeClassName ? activeClassName : className;

  // a button to be shown when user is signed out
  if (!isLoggedIn) {
    return (
      <a
        href={`/signup?tribe=${tribe.slug}`}
        type="button"
        className={`${className} tribe-join`}
      >
        {icon && (
          <>
            <i className="icon-plus" />{' '}
          </>
        )}
        {buttonLabel}
      </a>
    );
  }

  return (
    <TooltipWithContent
      tooltip={t('Leave circle') as string}
      placement="bottom"
      hidden={!isMember}
      id={`tribe-${tribe._id}`}
    >
      <button
        type="button"
        className={`${
          isMember ? 'btn-active' : ''
        } ${currentClassName} tribe-join`}
        disabled={isLoading}
        aria-label={ariaLabel}
        onClick={onToggle}
      >
        {icon && (
          <>
            <i className={isMember ? 'icon-ok' : 'icon-plus'} />{' '}
          </>
        )}
        {buttonLabel}
      </button>
    </TooltipWithContent>
  );
}

// @TODO this can (and should) be replaced by other container, when we finish the migration; when we start using redux etc.
export default function JoinButton({
  tribe,
  user,
  onUpdated,
  ...rest
}: JoinButtonProps) {
  // isLeaving controls whether the modal for leaving a tribe is shown
  const [isLeaving, setIsLeaving] = useState(false);

  const [isUpdating, setIsUpdating] = useState(false);

  const isMemberInitial = (user?.memberIds || []).includes(tribe._id);
  const [isMember, setIsMember] = useState(isMemberInitial);

  /**
   * Handle joining or leaving of a tribe
   * - Join: join tribe immediately (api call)
   * - Leave: show confirmation modal
   */
  async function handleToggleMembership() {
    if (isMember) {
      setIsLeaving(true);
    } else {
      // updating starts
      setIsUpdating(true);

      // join
      const data = await api.join(tribe._id);
      // update the membership locally
      setIsMember(true);

      // updating finished
      setIsUpdating(false);
      // tell the ancestor components that the membership was updated
      onUpdated?.(data);
    }
  }

  /**
   * Leave a tribe (api call)
   */
  async function handleLeave() {
    setIsUpdating(true);
    const data = await api.leave(tribe._id);
    setIsUpdating(false);
    setIsLeaving(false);
    setIsMember(false);
    // tell the ancestor components that the membership was updated
    onUpdated?.(data);
  }

  function handleCancelLeave() {
    setIsLeaving(false);
  }

  return (
    <>
      <LeaveTribeModal
        show={isLeaving}
        tribe={tribe}
        onConfirm={handleLeave}
        onCancel={handleCancelLeave}
      />
      <JoinButtonPresentational
        tribe={tribe}
        isLoggedIn={!!user}
        isMember={isMember}
        isLoading={isUpdating}
        {...rest}
        onToggle={handleToggleMembership}
      />
    </>
  );
}
